/** Rubrique Tags (Cave) : liste, édition (nom + couleur), fusion, suppression avec réaffectation. */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Trash2, Merge, Plus } from 'lucide-react';
import { caveApi } from '../../../services/api';
import { Button, Spinner, Card } from '../../../components/ui/kit';
import { InlineEditable } from '../../../components/wine/InlineEditable';

export default function Tags() {
  const qc = useQueryClient();
  const { data: tags = [], isLoading } = useQuery({ queryKey: ['tags'], queryFn: caveApi.tags });
  const [dialog, setDialog] = useState(null); // { mode:'delete'|'merge', name }
  const [target, setTarget] = useState('');
  const [newName, setNewName] = useState('');

  const refresh = () => qc.invalidateQueries({ queryKey: ['tags'] });
  const rename = async (name, value) => { await caveApi.updateTag(name, { name: value }); refresh(); };
  const recolor = async (name, color) => { await caveApi.updateTag(name, { color }); refresh(); };
  const add = async () => { const n = newName.trim(); if (!n) return; await caveApi.updateTag(n, { }); setNewName(''); refresh(); toast.success('Tag ajouté'); };
  const confirm = async () => {
    if (dialog.mode === 'delete') { await caveApi.deleteTag(dialog.name, target); toast.success('Tag supprimé'); }
    else { if (!target || target === dialog.name) return; await caveApi.mergeTags(dialog.name, target); toast.success('Tags fusionnés'); }
    setDialog(null); setTarget(''); refresh();
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="max-w-[620px]">
      <h2 className="font-title text-3xl mb-1">Tags</h2>
      <p className="text-[13px] text-n600 mb-4">Étiquettes libres pour classer les cuvées (apéritif, garde, coup de cœur…).</p>

      <div className="flex items-center gap-2 mb-4">
        <input className="vd-input h-9 flex-1" placeholder="Nouveau tag…" value={newName}
          onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} data-testid="tag-new-input" />
        <Button variant="primary" onClick={add} data-testid="tag-add"><Plus className="w-4 h-4" /> Ajouter</Button>
      </div>

      <Card className="divide-y divide-hair" data-testid="tags-list">
        {tags.map((t) => (
          <div key={t.name} className="flex items-center gap-3 px-4 py-2.5" data-testid={`tag-row-${t.name}`}>
            <span className="relative inline-flex items-center">
              <span className="w-5 h-5 rounded-full block border border-hair" style={{ background: t.color }} />
              <input type="color" value={t.color} onChange={(e) => recolor(t.name, e.target.value)} data-testid={`tag-color-${t.name}`}
                className="absolute inset-0 opacity-0 cursor-pointer w-5 h-5" title="Choisir la couleur" />
            </span>
            <span className="flex-1 text-[14px]">
              <InlineEditable value={t.name} onSave={(v) => rename(t.name, v)} testId={`tag-name-${t.name}`} />
            </span>
            <span className="text-[12px] text-n500 nums">{t.count} cuvée{t.count > 1 ? 's' : ''}</span>
            <button className="text-n500 hover:text-ink" title="Fusionner" data-testid={`tag-merge-${t.name}`}
              onClick={() => { setDialog({ mode: 'merge', name: t.name }); setTarget(''); }}><Merge className="w-4 h-4" /></button>
            <button className="text-n500 hover:text-red-600" title="Supprimer" data-testid={`tag-delete-${t.name}`}
              onClick={() => { setDialog({ mode: 'delete', name: t.name }); setTarget(''); }}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {tags.length === 0 && <p className="px-4 py-6 text-[13px] text-n500">Aucun tag pour le moment.</p>}
      </Card>

      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="tag-dialog">
          <div className="absolute inset-0 bg-ink/30" onClick={() => setDialog(null)} />
          <div className="relative bg-paper rounded-lg shadow-lg w-full max-w-sm p-5">
            {dialog.mode === 'delete' ? (
              <>
                <h3 className="font-title text-2xl mb-2">Supprimer « {dialog.name} »</h3>
                <p className="text-[13px] text-n600 mb-3">Les cuvées portant ce tag peuvent être réaffectées à un autre tag, ou simplement le perdre.</p>
                <select className="vd-input w-full h-9 mb-4" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="tag-reassign-select">
                  <option value="">Retirer simplement le tag</option>
                  {tags.filter((t) => t.name !== dialog.name).map((t) => <option key={t.name} value={t.name}>Réaffecter à « {t.name} »</option>)}
                </select>
              </>
            ) : (
              <>
                <h3 className="font-title text-2xl mb-2">Fusionner « {dialog.name} »</h3>
                <p className="text-[13px] text-n600 mb-3">Les cuvées de « {dialog.name} » reçoivent le tag cible ; « {dialog.name} » est supprimé.</p>
                <select className="vd-input w-full h-9 mb-4" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="tag-merge-select">
                  <option value="">Choisir le tag cible…</option>
                  {tags.filter((t) => t.name !== dialog.name).map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
                </select>
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button onClick={() => setDialog(null)} data-testid="tag-dialog-cancel">Annuler</Button>
              <Button variant="primary" onClick={confirm} data-testid="tag-dialog-confirm">Confirmer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
